/**
 * PrenotazioneForm Component - Hedita Design System
 * Feature: Prenotazioni - Form creazione/modifica
 * Layout: Drawer laterale (desktop) / Bottom Sheet (mobile)
 */

import { useState, useEffect } from "react";
import { X, CheckCircle, AlertCircle, ArrowLeft, CalendarDays, User, Home, CreditCard, Building2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { usePrenotazione, useCreatePrenotazione, useUpdatePrenotazione } from "../hooks";
import { useStrutture } from "@/features/strutture";
import type { Prenotazione } from "@/types";

const STATI = ["Attesa di conferma", "Confermata", "Check-in online effettuato", "In corso", "Check-out effettuato", "Completata", "Cancellata", "No show"];
const CANALI = ["Diretto", "Airbnb", "Booking.com", "Vrbo", "Expedia", "TripAdvisor", "Altro"];

interface PrenotazioneFormProps {
  id?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: (prenotazione: Prenotazione) => void;
}

export function PrenotazioneForm({ id, open, onOpenChange, onSaved }: PrenotazioneFormProps) {
  const isNew = !id || id === "nuovo";
  
  const { data: prenotazione, isLoading } = usePrenotazione(isNew ? undefined : id);
  const { data: strutture = [] } = useStrutture();
  const createMut = useCreatePrenotazione();
  const updateMut = useUpdatePrenotazione();

  const [form, setForm] = useState({
    ospite_nome: "",
    ospite_cognome: "",
    ospite_email: "",
    ospite_telefono: "",
    struttura_id: "",
    struttura_nome: "",
    check_in: "",
    check_out: "",
    adulti: 2,
    bambini: 0,
    importo_totale: "",
    caparra: "",
    canale: "Diretto",
    stato: "Confermata",
    note: "",
  });
  
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (prenotazione) {
      setForm({
        ospite_nome: prenotazione.ospite_nome || "",
        ospite_cognome: prenotazione.ospite_cognome || "",
        ospite_email: prenotazione.ospite_email || "",
        ospite_telefono: prenotazione.ospite_telefono || "",
        struttura_id: prenotazione.struttura_id || "",
        struttura_nome: prenotazione.struttura_nome || "",
        check_in: prenotazione.check_in || "",
        check_out: prenotazione.check_out || "",
        adulti: prenotazione.adulti || 2,
        bambini: prenotazione.bambini || 0,
        importo_totale: prenotazione.importo_totale?.toString() || "",
        caparra: prenotazione.caparra?.toString() || "",
        canale: prenotazione.canale || "Diretto",
        stato: prenotazione.stato || "Confermata",
        note: prenotazione.note || "",
      });
    }
  }, [prenotazione]);

  const set = (k: keyof typeof form, v: string | number) => setForm(f => ({ ...f, [k]: v }));

  const handleStrutturaChange = (strutturaId: string) => {
    const s = strutture.find(s => s.id === strutturaId);
    set("struttura_id", strutturaId);
    set("struttura_nome", s?.nome || "");
  };

  const handleSave = () => {
    if (!form.ospite_nome.trim()) return;
    setSaveStatus("saving");
    setSaveError(null);

    const data = {
      ...form,
      adulti: Number(form.adulti) || 2,
      bambini: Number(form.bambini) || 0,
      importo_totale: form.importo_totale ? parseFloat(form.importo_totale) : undefined,
      caparra: form.caparra ? parseFloat(form.caparra) : undefined,
    };

    const onSuccess = (res: Prenotazione) => {
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
      onSaved?.(res);
      if (isNew) onOpenChange(false);
    };
    
    const onError = (err: Error) => {
      setSaveStatus("error");
      setSaveError(err?.message ?? "Errore di salvataggio");
    };

    if (isNew) {
      createMut.mutate(data, { onSuccess, onError });
    } else {
      updateMut.mutate({ id: id!, data }, { onSuccess, onError });
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setSaveStatus("idle");
    setSaveError(null);
    if (isNew) {
      setForm({
        ospite_nome: "", ospite_cognome: "", ospite_email: "", ospite_telefono: "",
        struttura_id: "", struttura_nome: "", check_in: "", check_out: "",
        adulti: 2, bambini: 0, importo_totale: "", caparra: "",
        canale: "Diretto", stato: "Confermata", note: "",
      });
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent side="large" className="overflow-y-auto w-full sm:w-[90%]">
        <SheetHeader className="pb-4 border-b border-border">
          <SheetTitle className="text-lg font-bold text-foreground flex items-center gap-2">
            {isNew ? "Nuova prenotazione" : "Modifica prenotazione"}
          </SheetTitle>
        </SheetHeader>

        {!isNew && isLoading ? (
          <div className="py-8 space-y-4">
            <Skeleton className="h-24 rounded-xl" />
            <Skeleton className="h-24 rounded-xl" />
            <Skeleton className="h-24 rounded-xl" />
          </div>
        ) : (
          <div className="py-4 space-y-5">
            {/* Ospite */}
            <div className="bg-secondary/30 rounded-xl p-4 space-y-4 border border-border/50">
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-2">
                <User className="h-4 w-4 text-primary" />
                Dati ospite
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Nome *</label>
                  <input 
                    className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                    value={form.ospite_nome}
                    onChange={e => set("ospite_nome", e.target.value)}
                    placeholder="Mario"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Cognome</label>
                  <input 
                    className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                    value={form.ospite_cognome}
                    onChange={e => set("ospite_cognome", e.target.value)}
                    placeholder="Rossi"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Email</label>
                  <input 
                    type="email"
                    className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                    value={form.ospite_email}
                    onChange={e => set("ospite_email", e.target.value)}
                    placeholder="email@esempio.com"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Telefono</label>
                  <input 
                    className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                    value={form.ospite_telefono}
                    onChange={e => set("ospite_telefono", e.target.value)}
                    placeholder="+39 ..."
                  />
                </div>
              </div>
            </div>

            {/* Struttura e Date */}
            <div className="bg-secondary/30 rounded-xl p-4 space-y-4 border border-border/50">
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-2">
                <Building2 className="h-4 w-4 text-primary" />
                Struttura e date
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Struttura</label>
                <select 
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                  value={form.struttura_id}
                  onChange={e => handleStrutturaChange(e.target.value)}
                >
                  <option value="">Seleziona struttura...</option>
                  {strutture.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Check-in</label>
                  <input 
                    type="date"
                    className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                    value={form.check_in}
                    onChange={e => set("check_in", e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Check-out</label>
                  <input 
                    type="date"
                    className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                    value={form.check_out}
                    onChange={e => set("check_out", e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Adulti</label>
                  <input 
                    type="number"
                    min={1}
                    className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                    value={form.adulti}
                    onChange={e => set("adulti", e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Bambini</label>
                  <input 
                    type="number"
                    min={0}
                    className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                    value={form.bambini}
                    onChange={e => set("bambini", e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Pagamento */}
            <div className="bg-secondary/30 rounded-xl p-4 space-y-4 border border-border/50">
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-2">
                <CreditCard className="h-4 w-4 text-primary" />
                Pagamento
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Importo totale (€)</label>
                  <input 
                    type="number"
                    className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                    value={form.importo_totale}
                    onChange={e => set("importo_totale", e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Caparra (€)</label>
                  <input 
                    type="number"
                    className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                    value={form.caparra}
                    onChange={e => set("caparra", e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Canale</label>
                  <select 
                    className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                    value={form.canale}
                    onChange={e => set("canale", e.target.value)}
                  >
                    {CANALI.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Stato</label>
                  <select 
                    className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                    value={form.stato}
                    onChange={e => set("stato", e.target.value)}
                  >
                    {STATI.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Note */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Note</label>
              <textarea
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50 resize-none"
                rows={3}
                value={form.note}
                onChange={e => set("note", e.target.value)}
                placeholder="Note interne..."
              />
            </div>

            {/* Status */}
            {saveStatus === "saved" && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm">
                <CheckCircle className="h-4 w-4" /> Salvato con successo
              </div>
            )}
            {saveStatus === "error" && saveError && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm">
                <AlertCircle className="h-4 w-4" /> {saveError}
              </div>
            )}
          </div>
        )}

        <SheetFooter className="pt-4 border-t border-border flex gap-2">
          <button 
            onClick={handleClose}
            className="flex-1 py-2.5 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all"
          >
            Annulla
          </button>
          <button 
            onClick={handleSave}
            disabled={saveStatus === "saving" || !form.ospite_nome.trim()}
            className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-all disabled:opacity-50"
          >
            {saveStatus === "saving" ? "Salvataggio..." : isNew ? "Crea" : "Salva"}
          </button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
